import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SecureStorageService } from '../storage/secure-storage.service';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { FaceScanApiConfiguration } from 'src/app/api/proxy/facescan/face-scan-api-configuration';
import {
  scanSdkScanConfigPost,
  scanSubmitSdkSessionPost,
  scanScanCompletedSessionIdGet,
  scanRequestSessionPost,
  scanLastScanProfileIdGet,
  scanSessionResultSessionIdGet,
  scanBiometricHistoryProfileIdGet
} from 'src/app/api/proxy/facescan/functions';
import { Capacitor } from '@capacitor/core';
import {
  DiabetesType, ProfileDataModel,
  ProfileScanHistoryModel,
  RangeLevel,
  ScanSdkScanConfigRequestModel,
  ScanSdkScanResultModel, ScanSessionRequestModel, ScanSessionResultModel, SdkConfigModel, SdkErrorModel, SdkResultItemModel
} from 'src/app/api/proxy/facescan/facescan-models';
import { GenderType } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { firstValueFrom } from 'rxjs';
import { StorageKey } from '../storage/models/storage-key.enum';
import { LastFaceScan } from './LastFaceScan';
import Anura from 'src/app/plugins/anura/anura-plugin';
import { AnuraResponseModel } from 'src/app/plugins/anura/anura-response-model';
import { AnuraRequestModel } from 'src/app/plugins/anura/anura-request-model';
import { GaugeValueModel } from './GaugeValueModel';
import { BiometricService, GAUGE_COLORS } from '../biometric-service/biometric.service';
import { UserInfoService } from '../auth-service/user-info-service';
import { EnvironmentConfigService } from '../environment-config-service/environment-config.service';
import { EventService } from '../events/event.service';
import { ProfileModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { TranslatorService } from '../translator-service/translator.service';
import { LocaleService } from '../locale-service/locale.service';
import { SystemLogService } from '../systemlog-service/systemlog.service';
import { isAfter } from 'date-fns';
import { PromptService } from '../promtp-service/prompt.service';
import { ErrorCode } from './ErrorCode';
import { NavController } from '@ionic/angular';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { environment } from 'src/environments/environment';
import VivaScoreConnect from 'src/app/plugins/vivascore-connect/vivascore-connect-plugin';
import { BaseApiService } from '../base-api-service/base-api.service';

@Injectable({
  providedIn: 'root'
})
export class FacescanService extends BaseApiService {

  private _resultsRoute: string = 'face-scan-web-result'; //route to return scan and show results
  private _resultsTeamsRoute: string = 'assets/static/scan-complete.html'; //route to return scan and show results
  currentSessionId: string | undefined;
  private NETWORK_TIMEOUT = 60000;
  private NUMBER_OF_MONTHS_HISTORY = 12;

  constructor(
    protected _myProfileService: MyProfileService,
    private http: HttpClient,
    private faceScanCfg: FaceScanApiConfiguration,
    private _EnvironmentConfigService: EnvironmentConfigService,
    private _TranslatorService: TranslatorService,
    private _localeService: LocaleService,
    protected _PromptService: PromptService,
    protected _navCtrl: NavController,
    private _BiometricService: BiometricService) {

    super(_PromptService);
  }

  public async doMeasure(myProfile: ProfileModel,
    redirectPath: string | null = null): Promise<ScanConfigurationResult> {

    let result: ScanConfigurationResult = {
      isAllowed: false,
      errorMessage: this.getErrroMessage('UNKNOWN')
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return result;
    }

    await SecureStorageService.remove(StorageKey.NextFaceScanRefresh);
    this._nextRefresh = null;



    await SecureStorageService.remove(StorageKey.FaceScanResults);


    try {
      let scanConfig = await this.loadScanConfiguration(myProfile);
      if (scanConfig.isAllowed) {

        switch (scanConfig.scanProviderType) {
          case 0:
            if (Capacitor.getPlatform() == 'web')
              result = await this.doWebMeasure(myProfile, redirectPath);
            else
              result = await this.doNuralogixMeasure(scanConfig, myProfile);
            break;
          case 1:
            result = await this.doWebMeasure(myProfile, redirectPath);
            break;
          case 2:
          case 3:
            result = await this.doWebMeasure(myProfile, redirectPath);
            break;
        }
      }

    } catch {
      await this.getLastScanResult(false);
    }
    if (result.scanCompelted == false) {
      await this.getLastScanResult(false);
    }
    return result;
  }

  private async loadScanConfiguration(myProfile: ProfileModel): Promise<ScanConfigurationResult> {
    const result: ScanConfigurationResult = {
      isAllowed: false,
      errorMessage: this.getErrroMessage('UNKNOWN')
    }

    const scanProfile = await this.getLocalProfile(myProfile);

    const scanConfigRequest: ScanSdkScanConfigRequestModel = {
      profile: scanProfile,
      platform: Capacitor.getPlatform()
    };

    let emlAndPassword = await this.emailAndPassword(myProfile);
    if (emlAndPassword) {
      scanConfigRequest.reportPassword = emlAndPassword.reportPassword;
      scanConfigRequest.passwordEmailMessage = emlAndPassword.passwordEmailMessage;

    }

    let deviceInfo = await Device.getInfo();
    scanConfigRequest.deviceModel = deviceInfo.model;


    const env = await this._EnvironmentConfigService.EnsureNetworkConfiguration("FaceScan");
    if (!env)
      return result;


    const status = await Network.getStatus();
    if (!status.connected) {
      return result;
    }


    const obs = scanSdkScanConfigPost(this.http, this.faceScanCfg.rootUrl, { body: scanConfigRequest });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'FaceScanService.loadScanConfiguration.scanSdkScanConfigPost');
    const scanConfig = httpResponse ? httpResponse.body : null;
    if (scanConfig) {
      result.isAllowed = scanConfig.scanAllowed!;
      result.scanProviderType = scanConfig.scanProviderType ?? 0;
      result.scanDuration = scanConfig.scanDuration;

      result.partnerId = scanConfig.dfxPartnerId;
      result.applicationId = env!.faceScanConfig!.applicationId!;
      result.dfxLicenceKey = scanConfig.dfxLicenceKey;
      result.dfxStudyId = scanConfig.dfxStudyId;
      result.dfxRestUrl = scanConfig.dfxRestUrl;
      result.dfxWSUrl = scanConfig.dfxwsUrl;
      result.sessionId = scanConfig.sessionId;


      if (scanConfig.sessionId)
        this.currentSessionId = scanConfig.sessionId;

      switch (scanConfig.strictness) {
        case 1: result.strictness = 1; break;
        case 2: result.strictness = 2; break;
        case 3: result.strictness = 3; break;
        case 4: result.strictness = 4; break;
        case 5: result.strictness = 5; break;
        default: 1;
      }

      if (!scanConfig.scanAllowed) {
        result.errorMessage = this.getErrroMessage('SCAN_NOT_ALLOWED');
        result.isAllowed = false;
      }
    } else {
      result.errorMessage = this.getErrroMessage('NETWORK');
    }

    return result;
  }

  private async doNuralogixMeasure(sessionConfig: ScanConfigurationResult, myProfile: ProfileModel): Promise<ScanConfigurationResult> {

    const env = await this._EnvironmentConfigService.EnsureNetworkConfiguration("FaceScan");
    if (!env)
      return sessionConfig;


    const status = await Network.getStatus();
    if (!status.connected) {
      return sessionConfig;
    }

    const scanProfile = await this.getLocalProfile(myProfile);
    const heightInCm = this.toIntTrunc(scanProfile.heightInCm);
    const weightInKg = this.toIntTrunc(scanProfile.weightInKg);
    const request: AnuraRequestModel = {
      ageInYears: scanProfile.ageInYears!,
      gender: scanProfile.gender!.toString().toLocaleLowerCase(),
      smoking: scanProfile.smoking!,
      diabetes: 0,
      bloodpressuremeds: scanProfile.bloodPressureMedication!,

      partnerId: sessionConfig.partnerId,
      applicationId: env!.faceScanConfig!.applicationId!,
      dfxLicenceKey: sessionConfig.dfxLicenceKey,
      dfxStudyId: sessionConfig.dfxStudyId,
      dfxRestUrl: sessionConfig.dfxRestUrl,
      dfxWSUrl: sessionConfig.dfxWSUrl,
      ...(heightInCm != null ? { heightInCm } : {}),
      ...(weightInKg != null ? { weightInKg } : {}),
      language: this._localeService.getCulture(),
    };

    if (scanProfile.diabetes == DiabetesType.Type1) request.diabetes = 1;
    else if (scanProfile.diabetes == DiabetesType.Type2) request.diabetes = 2;

    const requestString = JSON.stringify(request);
    const response = await Anura.performScan({ value: requestString });
    const responseString = response.value;
    sessionConfig.scanCompelted = false;

    if (!responseString.includes('CANCELLED')) {
      const resultData: Array<AnuraResponseModel> = JSON.parse(responseString);

      if (resultData.length > 0) {
        const measurementId = resultData[0].measurementId;
        const arrayItems: SdkResultItemModel[] = [];
        let hasError = false;
        let errorMessage = null;
        resultData.forEach((g) => {
          const itm: SdkResultItemModel = {
            key: g.key,
            value: g.value,
          };
          arrayItems.push(itm);
          if (g.key == "SNR" && g.value < 0) {
            hasError = true,
              errorMessage = this.getErrroMessage(g.key);
          }
        });

        const sdkScanResultModel: ScanSdkScanResultModel = {
          measurementId: measurementId,
          profile: scanProfile,
          scanResults: arrayItems,
          errorMessage: errorMessage,
          platform: Capacitor.getPlatform()
        };

        let emlAndPassword = await this.emailAndPassword(myProfile);
        if (emlAndPassword) {
          sdkScanResultModel.reportPassword = emlAndPassword.reportPassword;
          sdkScanResultModel.passwordEmailMessage = emlAndPassword.passwordEmailMessage;
        }

        const obs = scanSubmitSdkSessionPost(this.http, this.faceScanCfg.rootUrl, { body: sdkScanResultModel });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'FaceScanService.doNuralogixMeasure.scanSubmitSdkSessionPost');
    const sessionData = httpResponse ? httpResponse.body : null;
        if (sessionData) {
          if (!hasError) {
            await this.updateLasScanResults(sessionData, true);
            sessionConfig.scanCompelted = true;
            return sessionConfig;
          } else {
            sessionConfig.errorMessage = this.getErrroMessage('SNR');
            return sessionConfig;
          }
        } else {
          sessionConfig.errorMessage = this.getErrroMessage('NETWORK');
          return sessionConfig;
        }

      } else {

        const measurementId = "NONE";
        const errorReason = "No Results";

        const sdkScanResultModel: ScanSdkScanResultModel = {
          measurementId: measurementId,
          profile: scanProfile,
          scanResults: null,
          errorMessage: errorReason,
          platform: Capacitor.getPlatform()
        };

        const obs2 = scanSubmitSdkSessionPost(this.http, this.faceScanCfg.rootUrl, { body: sdkScanResultModel });
        const httpResponse2 = await this.callApi(obs2, this.NETWORK_TIMEOUT, 'FaceScanService.doNuralogixMeasure.scanSubmitSdkSessionPost.noResults');
        const submitResult = httpResponse2 ? httpResponse2.body : null;
        if (submitResult) {
          sessionConfig.errorMessage = this.getErrroMessage('SNR');
          return sessionConfig;
        } else {
          sessionConfig.errorMessage = this.getErrroMessage('NETWORK');
          return sessionConfig;
        }
      }
    } else {
      //split the reason by "|"
      const parts = responseString.split('|');

      let measurementId = "NONE";
      if (parts && parts.length > 1) {
        if (parts[1] && parts[1].length > 0) {
          measurementId = parts[1];
        }
      }

      let errorReason = "Cancelled";
      if (parts && parts.length > 2) {

        if (parts[1] && parts[1].length > 0) {
          errorReason = parts[2];
        }
      }

      const sdkScanResultModel: ScanSdkScanResultModel = {
        measurementId: measurementId,
        profile: scanProfile,
        scanResults: null,
        errorMessage: errorReason,
        platform: Capacitor.getPlatform()
      };

      sessionConfig.errorMessage = this.getErrroMessage('TIMEOUT');
      if (errorReason == "FAILSAFETIMEOUT")
        sessionConfig.errorMessage = this.getErrroMessage('CANCELLED');


      const obs3 = scanSubmitSdkSessionPost(this.http, this.faceScanCfg.rootUrl, { body: sdkScanResultModel });
      const httpResponse3 = await this.callApi(obs3, this.NETWORK_TIMEOUT, 'FaceScanService.doNuralogixMeasure.scanSubmitSdkSessionPost.cancelled');
      const submitResult = httpResponse3 ? httpResponse3.body : null;
      if (submitResult) {
        sessionConfig.errorMessage = this.getErrroMessage('SNR');
        return sessionConfig;
      } else {
        sessionConfig.errorMessage = this.getErrroMessage('NETWORK');
        return sessionConfig;
      }
    }

    return sessionConfig;
  }

  public currentSessionIdGet(): string | null {
    return this.currentSessionId!;
  }

  public async scanInProgress(): Promise<boolean> {
    if (!this.currentSessionId)
      return false;

    const status = await Network.getStatus();
    if (!status.connected) {
      return false;
    }

    const obs6 = scanScanCompletedSessionIdGet(this.http, this.faceScanCfg.rootUrl, { sessionId: this.currentSessionId });
    const httpResponse6 = await this.callApi(obs6, this.NETWORK_TIMEOUT, 'FaceScanService.scanInProgress.scanScanCompletedSessionIdGet');
    const response = httpResponse6 ? httpResponse6.body : null;
    if (response !== null) {
      return response == false;
    } else {
      return true;
    }

  }


  private async emailAndPassword(myProfile: ProfileModel): Promise<any> {
    if (myProfile.dateOfBirth) {
      const date = myProfile.dateOfBirth;
      const country = await this._EnvironmentConfigService.CurrentCountry();

      let isUSCountry = false;
      if (country && country.countryCode && country.countryCode.toLocaleLowerCase() == 'us')
        isUSCountry = true;

      // Format the date as yyyyMMdd
      const reportPassword = this.formatDateToString(date, isUSCountry);
      const envMessageOverride = environment.faceScanPasswordEmailMessageOverride;
     
      const hasDefaultOverride = envMessageOverride !== undefined && envMessageOverride !== null;
      const hasUSOverride = hasDefaultOverride;

      const buildDefaultMessage = () => {
        const greeting = this._TranslatorService.translateText('face-scan-service.messages.email-greeting-with-name')
          .replace('{name}', myProfile.firstName);
        return '<p>' + greeting + '</p>' +
        '<p>' + this._TranslatorService.translateText('face-scan-service.messages.email-intro') + '</p>' +
        '<p>' + this._TranslatorService.translateText('face-scan-service.messages.email-intro-2') + '</p>' +
        '<p>' + this._TranslatorService.translateText('face-scan-service.messages.email-password-block') + '</p>';
      };

      const buildUSMessage = () => {
        const greeting = this._TranslatorService.translateText('face-scan-service.messages.email-greeting-with-name')
          .replace('{name}', myProfile.firstName);
        return '<p>' + greeting + '</p>' +
        '<p>' + this._TranslatorService.translateText('face-scan-service.messages.email-intro') + '</p>' +
        '<p>' + this._TranslatorService.translateText('face-scan-service.messages.email-intro-2') + '</p>' +
        '<p>' + this._TranslatorService.translateText('face-scan-service.messages.email-password-block-us') + '</p>';
      };

      let passwordEmailMessage : string | null = isUSCountry
        ? (hasUSOverride ? envMessageOverride! : buildUSMessage())
        : (hasDefaultOverride ? envMessageOverride! : buildDefaultMessage());

        if(passwordEmailMessage == '')
          passwordEmailMessage = null;

      return { reportPassword, passwordEmailMessage };
    }


    return null;

  }

  private formatDateToString(dateInput: any, isUSCountry: boolean): string {
    // Ensure dateInput is a valid Date object
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date format: " + dateInput);
    }

    // Use UTC so the formatted date is consistent regardless of device timezone
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    if (!isUSCountry)
      return `${year}${month}${day}`;
    else
      return `${month}${day}${year}`;
  }


  private async doWebMeasure(myProfile: ProfileModel, redirectPath: string | null = null): Promise<ScanConfigurationResult> {

    const result: ScanConfigurationResult = {
      isAllowed: false,
      scanCompelted: false,
      errorMessage: this.getErrroMessage('UNKNOWN')
    }

    const env = this._EnvironmentConfigService.EnsureNetworkConfiguration("FaceScan");
    if (!env) {
      return result;
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return result;
    }


    let callback = window.location.origin;
    callback = callback.replace('#', '');
    callback = callback.replace('?retry=true', '');
    callback = callback.replace('/home', '');
    if (!callback.endsWith('/')) callback = callback + '/';

    callback = callback + this._resultsRoute;

    let currentPath = window.location.pathname;

    // Remove the leading '/' from the path
    if (currentPath.startsWith('/')) {
      currentPath = currentPath.substring(1);
    }

    if (!redirectPath)
      redirectPath = currentPath;


    //add a ? to the end of the url to point back to my current page
    callback = callback + '?redirectpath=' + redirectPath;

    const scanProfile = await this.getLocalProfile(myProfile);
    const sessionRequest: ScanSessionRequestModel = {
      callbackURL: callback,
      profile: scanProfile,
      platform: Capacitor.getPlatform()
    };

    sessionRequest.deviceModel = (await Device.getInfo()).model;

    await SecureStorageService.remove(StorageKey.CurrentScanSessionId);

    let emlAndPassword = await this.emailAndPassword(myProfile);
    if (emlAndPassword) {
      sessionRequest.reportPassword = emlAndPassword.reportPassword;
      sessionRequest.passwordEmailMessage = emlAndPassword.passwordEmailMessage;
    }

    const obs7 = scanRequestSessionPost(this.http, this.faceScanCfg.rootUrl, { body: sessionRequest });
    const httpResponse7 = await this.callApi(obs7, this.NETWORK_TIMEOUT, 'FaceScanService.doWebMeasure.scanRequestSessionPost');
    const callResult = httpResponse7 ? httpResponse7.body : null;
    if (callResult) {
      if (callResult.scanAllowed == true) {
        if (callResult.redirectUrl) {
          this.currentSessionId = callResult.sessionId;
          //save object in localstorage using the profile identifier as identifier
          await SecureStorageService.set(StorageKey.CurrentScanSessionId, callResult.sessionId!);
          window.location.href = callResult.redirectUrl;
          result.scanCompelted = true;
        }
      } else {
        result.errorMessage = this.getErrroMessage('SCAN_NOT_ALLOWED');
      }
    } else {
      result.errorMessage = this.getErrroMessage('NETWORK');
    }
    return result;
  }

  public async getLastScanResultStatic(): Promise<LastFaceScan | null> {
    let model: LastFaceScan | null = null;
    const scanResult = await SecureStorageService.get(StorageKey.FaceScanResults);
    if (scanResult) {
      try {
        model = JSON.parse(scanResult);
      } catch {
        model = null
      }
    }

    if (model && !model.nextScanAllowed)
      model.scanAllowed = true;

    if (model) {
      EventService.faceScanUpdated.emit(model);

    }
    return model;
  }
  private _nextRefresh: Date | null = null;
  public async getLastScanResult(forced: boolean): Promise<LastFaceScan | null> {
    const tempdt = await SecureStorageService.get(StorageKey.NextFaceScanRefresh);
    if (tempdt) {
      this._nextRefresh = new Date(tempdt);
    }

    let model = await this.getLastScanResultStatic();

    if (!forced && model) {
      if (this._nextRefresh && !isAfter(new Date(), this._nextRefresh)) {
        return model;
      }
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return model;
    }

    const userInfo = await UserInfoService.getUserInfo();
    if (!userInfo) {
      return model;
    }
    const obs8 = scanLastScanProfileIdGet(this.http, this.faceScanCfg.rootUrl, { profileId: userInfo?.tokenClaims.personId });
    const httpResponse8 = await this.callApi(obs8, this.NETWORK_TIMEOUT, 'FaceScanService.getLastScanResult.scanLastScanProfileIdGet');
    const lastScan = httpResponse8 ? httpResponse8.body : null;
    if (lastScan) {
      model = await this.updateLasScanResults(lastScan, false)
    } else {
      model = null;
    }
    EventService.faceScanUpdated.emit(model);
    return model;
  }

  public getErrroMessage(errorCode: string | null | undefined): string {
    if (errorCode) {
      switch (errorCode.toUpperCase()) {
        case 'MEASUREMENT_TIMED_OUT': return this._TranslatorService.translateText("face-scan-service.errors.MEASUREMENT_TIMED_OUT"); break;
        case 'MEASUREMENT_CANCELED': return this._TranslatorService.translateText("face-scan-service.errors.MEASUREMENT_CANCELED"); break;
        case 'CANCELED': return this._TranslatorService.translateText("face-scan-service.errors.MEASUREMENT_CANCELED"); break;
        case 'TOKEN_EXPIRED': return this._TranslatorService.translateText("face-scan-service.errors.TOKEN_EXPIRED"); break;
        case 'CAMERA_PERMISSION_NOT_GRANTED': return this._TranslatorService.translateText("face-scan-service.errors.CAMERA_PERMISSION_NOT_GRANTED"); break;
        case 'SNR': return this._TranslatorService.translateText("face-scan-service.errors.SNR"); break;
        case 'INVALID_BMI': return this._TranslatorService.translateText("face-scan-service.errors.INVALID_BMI"); break;
        case 'INTERRUPTED': return this._TranslatorService.translateText("face-scan-service.errors.INTERRUPTED"); break;
        case 'FACEMISSING': return this._TranslatorService.translateText("face-scan-service.errors.FACEMISSING"); break;
        case 'LOW_SNR': return this._TranslatorService.translateText("face-scan-service.errors.LOW_SNR"); break;
        case 'INVALID_HEIGHT': return this._TranslatorService.translateText("face-scan-service.errors.INVALID_HEIGHT"); break;
        case 'FACEOFFTARGET': return this._TranslatorService.translateText("face-scan-service.errors.FACEOFFTARGET"); break;
        case 'INVALID_AGE': return this._TranslatorService.translateText("face-scan-service.errors.INVALID_AGE"); break;
        case 'SCAN_CANCELLED': return this._TranslatorService.translateText("face-scan-service.errors.SCAN_CANCELLED"); break;
        case 'CAMERA_PERMISSION_DENIED': return this._TranslatorService.translateText("face-scan-service.errors.CAMERA_PERMISSION_DENIED"); break;
        case 'NETWORK': return this._TranslatorService.translateText("face-scan-service.errors.NETWORK"); break;
        case 'SCAN_NOT_ALLOWED': return this._TranslatorService.translateText("face-scan-service.errors.SCAN_NOT_ALLOWED"); break;

      }
    }
    return this._TranslatorService.translateText("face-scan-service.errors.UNKNOWN");

  }

  public async processWebResponse(sessionId: string): Promise<ScanConfigurationResult> {
    const callResult: ScanConfigurationResult = {
      isAllowed: false,
      errorMessage: this.getErrroMessage('UNKNOWN')
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return callResult;
    }


    const obs9 = scanSessionResultSessionIdGet(this.http, this.faceScanCfg.rootUrl, { sessionId: sessionId });
    const httpResponse9 = await this.callApi(obs9, this.NETWORK_TIMEOUT, 'FaceScanService.processWebResponse.scanSessionResultSessionIdGet');
    const result = httpResponse9 ? httpResponse9.body : null;
    if (result) {
      if (result.snr && result.snr < 0) {
        result.errorCode = 'SNR';
        result.hasError = true;
      }

      if (result.hasError == true || !result.measurements || result.measurements.length == 0) {
        callResult.errorMessage = this.getErrroMessage(result.errorCode);
        return callResult;
      } else {
        await this.updateLasScanResults(result, true);
        callResult.scanCompelted = true;
        this._nextRefresh = null;
        return callResult;
      }
    } else {
      callResult.errorMessage = this.getErrroMessage('NETWORK');
      return callResult;
    }

    return callResult;


  }

  async getLocalProfile(myProfile: ProfileModel): Promise<ProfileDataModel> {
    const userInfo = await UserInfoService.getUserInfo();

    let gender: GenderType | undefined = undefined;
    if (myProfile && myProfile.gender) {
      gender = myProfile.gender.toString() as GenderType;
    }

    const profileDataModel: ProfileDataModel = {
      ageInYears: this._myProfileService.getAge(myProfile),
      bloodPressureMedication: myProfile.bloodPressureMedication,
      diabetes: myProfile.diabetes as DiabetesType,
      emailAddress: userInfo?.email,
      gender: gender != null ? (gender.toString().toLowerCase() as 'male' | 'female') : undefined,
      heightInCm: this.toIntTrunc(myProfile.heightInCm),
      identifier: userInfo?.tokenClaims.personId,
      personName: myProfile.firstName + ' ' + myProfile.lastName,
      smoking: myProfile.smoking,
      weightInKg: this.toDecimal(myProfile.weightInKg),
      
      
    }
    // Try populate last known location from native plugin (if available)
    try {
      if (Capacitor.isNativePlatform()) {
        const loc = await VivaScoreConnect.getLastLocation();
        if (loc) {
          profileDataModel.latitude = loc.latitude ?? null;
          profileDataModel.longitude = loc.longitude ?? null;
          profileDataModel.locationDateTimeStamp = loc.timestamp ?? null;
          profileDataModel.locationCity = loc.city ?? null;
          profileDataModel.locationRegion = loc.region ?? null;
          profileDataModel.locationCountry = loc.country ?? null;
          profileDataModel.locationPostal = loc.postal ?? null;
          profileDataModel.locationTimezone = loc.timezone ?? null;
        }
      } else {
        // Web: leave as nulls
        profileDataModel.latitude = null;
        profileDataModel.longitude = null;
        profileDataModel.locationDateTimeStamp = null;
        profileDataModel.locationCity = null;
        profileDataModel.locationRegion = null;
        profileDataModel.locationCountry = null;
        profileDataModel.locationPostal = null;
        profileDataModel.locationTimezone = null;
      }
    } catch (error) {
      // Plugin not available or call failed; leave nulls
      profileDataModel.latitude = null;
      profileDataModel.longitude = null;
      profileDataModel.locationDateTimeStamp = null;
      profileDataModel.locationCity = null;
      profileDataModel.locationRegion = null;
      profileDataModel.locationCountry = null;
      profileDataModel.locationPostal = null;
      profileDataModel.locationTimezone = null;
    }
    return profileDataModel;

  }

  private async updateLasScanResults(sessionData: ScanSessionResultModel, isNewScan: boolean): Promise<LastFaceScan> {
    // Note: We intentionally ignore healthScore from the face scan API response.
    // Health score is only retrieved from the datapoints API (via BiometricService.getSummary).
    // The sessionData.healthScore property is not used to ensure we have a single source of truth.
    const lastFaceScan: LastFaceScan = {
      scanDateUTC: sessionData.scanDate ? sessionData.scanDate : '',
      measurements: sessionData.measurements,
      snr: sessionData.snr!,
      sessionId: sessionData.id,
      nextScanAllowed: sessionData.nextScanAllowed,
      scanAllowed: sessionData.scanAllowed
    }


    if (lastFaceScan && !lastFaceScan.nextScanAllowed)
      lastFaceScan.scanAllowed = true;

    const jsonResults = JSON.stringify(lastFaceScan);
    await SecureStorageService.set(StorageKey.FaceScanResults, jsonResults);
    this._nextRefresh = new Date(new Date().getTime() + 1000 * 60 * 60 * 2);//2 hours
    await SecureStorageService.set(StorageKey.NextFaceScanRefresh, this._nextRefresh.toISOString());

    if (isNewScan) {
      // ✅ CHANGE 1: Refresh health score

      // ✅ CHANGE 2: Force refresh of biometric data after scan completion
      // This fetches latest observations and emits onBiometricListUpdated event
      await this._BiometricService.getSummary(true);
    }
    EventService.faceScanUpdated.emit(lastFaceScan);
    //setTimeout(async () => { await this.updateScanHistory(); }, 1000);
    return lastFaceScan;
  }

  private formatValue(value: number | undefined): number | undefined {
    if (!value) return value;
    const hasDecimal = value % 1 !== 0;
    const val = hasDecimal ? value.toFixed(1) : value.toString();
    return +val;
  }

  public async renderScanResults(lastScanResults: LastFaceScan): Promise<GaugeValueModel[]> {

    let reportGauges: GaugeValueModel[] = [];

    if (lastScanResults && lastScanResults.measurements) {
      lastScanResults.measurements.forEach((m) => {
        const g: GaugeValueModel = {
          key: m.keyName!,
          useGauge: false,
          arcs: [],
          signalName: m.signalName,
          signalDescription: m.signalDescription,
          unit: m.unit,
          value: this.formatValue(m.value),
          info: m.additionalInfo,
          healthCloudArticleId: m.healthCloudArticleId,
          orderNumber: m.displayOrderNumber,
          references: m.references,
          level: RangeLevel.Poor,
          acceptedRange: m.acceptedRange,
          previousValue: m.previousValue,
          showAlert: m.alarmRaised,
          eventDate: lastScanResults.scanDateUTC

        };


        if (m.ranges && m.ranges.length > 0) {
          g.useGauge = true;
          m.ranges.forEach((r) => {
            g.arcs.push({
              min: r.minValue!,
              max: r.maxValue!,
              rangeLevel: r.rangeLevel!,
              color: GAUGE_COLORS[r.rangeLevel!].enabled,
              disabledColor: GAUGE_COLORS[r.rangeLevel!].disabled
            });
          });

          if (m.value) {
            const range = m.ranges.find((r) => m.value! >= r.minValue! && m.value! < r.maxValue!);
            if (range) {
              g.level = range.rangeLevel!;
            }
          }
        }
        reportGauges.push(g);
      });
    }

    const noGauge = reportGauges.filter((g) => !g.useGauge);
    const withGauge = reportGauges.filter((g) => g.useGauge);

    withGauge.sort((g, c) => {
      if (!g.orderNumber)
        g.orderNumber = 1000;
      if (!c.orderNumber)
        c.orderNumber = 1000;
      return g.orderNumber - c.orderNumber;
    });

    noGauge.sort((g, c) => {
      if (!g.orderNumber)
        g.orderNumber = 1000;
      if (!c.orderNumber)
        c.orderNumber = 1000;
      return g.orderNumber - c.orderNumber;
    });

    reportGauges = [];

    withGauge.forEach((g) => {
      reportGauges.push(g);
    });

    noGauge.forEach((g) => {
      reportGauges.push(g);
    });

    return reportGauges;
  }

  public async lastScanHistory(forced: boolean): Promise<ProfileScanHistoryModel | null> {

    //read the last scan from cache
    let faceScanModel: LastFaceScan | null = null;
    const lastScanResult = await SecureStorageService.get(StorageKey.FaceScanResults);
    if (lastScanResult) {
      try {
        faceScanModel = JSON.parse(lastScanResult);
      } catch {
        faceScanModel = null
      }
    }



    let model: ProfileScanHistoryModel | null = null;
    const scanResult = await SecureStorageService.get(StorageKey.FaceScanHistory);
    if (scanResult) {
      try {
        model = JSON.parse(scanResult);
      } catch {
        model = null
      }
    }

    if (model && faceScanModel) {
      const lastSessionId = (model as any).lastSessionId;
      if (lastSessionId && faceScanModel.sessionId === lastSessionId) {
        EventService.faceScanHistoryUpdated.emit(model);
        return model;
      }
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return model;
    }

    const userInfo = await UserInfoService.getUserInfo();
    if (!userInfo) {
      return null;
    }
    const obs10 = scanBiometricHistoryProfileIdGet(this.http, this.faceScanCfg.rootUrl, { profileId: userInfo?.tokenClaims.personId, numberOfMonths: this.NUMBER_OF_MONTHS_HISTORY });
    const httpResponse10 = await this.callApi(obs10, this.NETWORK_TIMEOUT, 'FaceScanService.lastScanHistory.scanBiometricHistoryProfileIdGet');
    const scanHistory = httpResponse10 ? httpResponse10.body : null;
    if (scanHistory) {
      (scanHistory as any).lastSessionId = faceScanModel?.sessionId;
      await SecureStorageService.set(StorageKey.FaceScanHistory, JSON.stringify(scanHistory));
      EventService.faceScanHistoryUpdated.emit(scanHistory);
      return scanHistory;
    }

    return model;

  }

  private getErrorCodeName(errorCode: ErrorCode): string {
    return ErrorCode[errorCode]; // Reverse mapping: Enum value to key
  }

  public async handleErrorAndRedirect(errorCode: ErrorCode, logError: string | null = null) {
    const errorCodeName = this.getErrorCodeName(errorCode); // Convert to strin

    let sdkScanResultModel: SdkErrorModel = {
      errorCode: errorCodeName,
      sessionId: this.currentSessionId!,
      message: logError
    };


    let scanResult: ScanConfigurationResult = {
      errorMessage: '',
      isAllowed: false,
      scanCompelted: false
    };

    const status = await Network.getStatus();
    if (!status.connected) {
      return;
    }


    const obs = scanSubmitSdkSessionPost(this.http, this.faceScanCfg.rootUrl, { body: sdkScanResultModel });
    await this.callApi(obs, this.NETWORK_TIMEOUT, 'FaceScanService.handleErrorAndRedirect.scanSubmitSdkSessionPost');


    await this._navCtrl.navigateForward('v2/health-check/scan-completed', {
      queryParams: {
        scanResult: scanResult
      }
    });

  }

  private toIntTrunc(v: any, fallback = 0): number {
    if (v === null || v === undefined)
      return fallback;
    const n = Number(String(v).trim().replace(',', '.'));
    if (!Number.isFinite(n))
      return fallback;
    return Math.trunc(n);
  }

  private toDecimal(v: any, fallback = 0): number {
    if (v === null || v === undefined)
      return fallback;
    const n = Number(String(v).trim().replace(',', '.'));
    if (!Number.isFinite(n))
      return fallback;
    return Math.round(n * 100) / 100; // Preserve 2 decimal places
  }

}

export interface ScanConfigurationResult {
  dfxWSUrl?: string | null | undefined;
  dfxRestUrl?: string | null | undefined;
  dfxStudyId?: string | null | undefined;
  dfxLicenceKey?: string | null | undefined;
  applicationId?: string | null | undefined;
  partnerId?: string | null | undefined;
  strictness?: 1 | 2 | 3 | 4 | 5 | undefined;
  scanDuration?: number | null | undefined;
  scanProviderType?: number | undefined;
  isAllowed: boolean;
  errorMessage: string | null;
  scanCompleteBefore?: null | string;
  scanCompelted?: boolean | null;
  sessionId?: string | null;
}
